import { cellValue } from './tableData';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sanitizeSheetName(name) {
  const s = (name || 'Report').slice(0, 31).replace(/[\\/*?:[\]]/g, ' ');
  return s || 'Report';
}

function toTitleCaseWords(value) {
  return String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function sanitizeFilePart(value) {
  return String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildExportFileName({ companyName, filename, sheetName, reportName }) {
  const companyPart = sanitizeFilePart(toTitleCaseWords(companyName || 'Paytiemp'));
  const rawReport = reportName || sheetName || filename || 'Report';
  const reportPart = sanitizeFilePart(toTitleCaseWords(rawReport));
  const datePart = new Date().toISOString().slice(0, 10);
  return `${companyPart} - ${reportPart} - ${datePart}.xls`;
}

/**
 * Export rows using the same column defs as the insight popup (HTML Excel = proper columns).
 */
export function exportToExcel({ filename, sheetName, columns, rows, companyName, reportName }) {
  if (!rows?.length) {
    alert('No data to export');
    return;
  }
  if (!columns?.length) {
    alert('No columns defined for export');
    return;
  }

  const title = sanitizeSheetName(sheetName || filename);
  const headerCells = [{ label: 'Sl No' }, ...columns]
    .map(col => `<th>${escapeHtml(col.label)}</th>`)
    .join('');

  const bodyRows = rows
    .map((row, idx) => {
      const cells = [`<td>${escapeHtml(idx + 1)}</td>`, ...columns
        .map(col => {
          const raw = cellValue(col, row);
          const text = raw === '—' ? '' : raw;
          return `<td>${escapeHtml(text)}</td>`;
        })].join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head>
<meta charset="UTF-8"/>
<!--[if gte mso 9]><xml>
<x:ExcelWorkbook xmlns:x="urn:schemas-microsoft-com:office:excel">
<x:ExcelWorksheets>
<x:ExcelWorksheet>
<x:Name>${escapeHtml(title)}</x:Name>
<x:WorksheetOptions><x:DoNotDisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet>
</x:ExcelWorksheets>
</x:ExcelWorkbook>
</xml><![endif]-->
<style>
table { border-collapse: collapse; width: 100%; font-family: Calibri, Arial, sans-serif; font-size: 11pt; border: 0.5pt solid #cbd5e1; }
th { background: #2563eb; color: #ffffff; font-weight: bold; text-align: left; padding: 8px 10px; white-space: nowrap; border: 0.5pt solid #cbd5e1; }
td { padding: 6px 10px; vertical-align: top; border: 0.5pt solid #cbd5e1; }
tr:nth-child(even) td { background: #f8fafc; }
</style>
</head>
<body>
<table>
<thead><tr>${headerCells}</tr></thead>
<tbody>${bodyRows}</tbody>
</table>
</body>
</html>`;

  const blob = new Blob([`\uFEFF${html}`], {
    type: 'application/vnd.ms-excel;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildExportFileName({ companyName, filename, sheetName, reportName });
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
